import { useState } from "react";
import { CommunityService } from "../_service";
import { ToastTypes } from "../contexts/toastr.context";
import I18 from "./atoms/i18";

/**
 * "Try v2" beta banner. Shown only to users the admin has granted access
 * (user.v2AccessEnabled, delivered on the /profile payload). On click it asks
 * v1 for a short-lived, single-use handoff URL and redirects to the v2 app,
 * where the user lands already logged in as themselves.
 */
export default function V2Banner({
  user,
  community,
  addToast = (_m: string, _s?: string, _t?: any) => {},
}: {
  user?: any;
  community?: any;
  addToast?: (message: string, sub?: string, type?: any) => void;
}) {
  const [loading, setLoading] = useState(false);

  // Master switch: if the community turned the v2 banner off, nobody sees it.
  // When on, it shows to users granted v2 access, plus community admins.
  const bannerEnabled = Boolean(community?.configuration?.isV2BannerEnabled);
  const isCommunityAdmin = community?.myRole === "admin";
  if (!bannerEnabled || (!user?.v2AccessEnabled && !isCommunityAdmin)) {
    return null;
  }

  const goToV2 = () => {
    if (loading) return;
    setLoading(true);
    CommunityService.getV2HandoffUrl(user, community?.id)
      .then(({ redirectUrl }: { redirectUrl?: string }) => {
        if (!redirectUrl) throw new Error("missing redirectUrl");
        window.location.href = redirectUrl;
      })
      .catch(() => {
        setLoading(false);
        addToast(
          "Couldn't open the new experience. Please try again.",
          "",
          ToastTypes.danger
        );
      });
  };

  return (
    <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
      <div className="flex items-center gap-2.5 text-sm">
        <span className="text-lg leading-none">✨</span>
        <span>
          <span className="font-semibold text-secondary-foreground">
            <I18>The new beSocial is ready for you.</I18>
          </span>{" "}
          <span className="text-secondary-foreground/70">
            <I18>
              Preview the redesigned experience — your account comes with you.
            </I18>
          </span>
        </span>
      </div>
      <button
        type="button"
        onClick={goToV2}
        disabled={loading}
        className="flex-none whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
        {loading ? <I18>Opening…</I18> : <I18>Try it now →</I18>}
      </button>
    </div>
  );
}
